/**
 * useNoteCopy - Business logic hook for note section copying
 *
 * Handles:
 * - Copying selected or all note sections
 * - Building HTML and plain text representations
 * - Applying user copy/paste preferences
 * - Clipboard operations with rich text support
 * - Success/error notifications
 * - Event tracking (Statsig + new telemetry system)
 *
 * Extracted from NoteViewRevamp.tsx handleCopySections implementation
 */

import { useState } from 'react';
import { type Encounter } from '@features/encounters';
import { useUserPreferences } from '@features/user-preferences';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';
import type { NoteResponse } from '@store/generated/notes';
import { buildSelectionCopied, type TelemetryContext } from '@utils/analytics/noteTelemetryBuilder';
import type { AnyBlockNoteEditor } from '@utils/blocknote/types';
import { copyRichText, formatDate, formatDuration } from '@utils/utils';
import { marked } from 'marked';
import { useSnackbar } from 'notistack';
import removeMd from 'remove-markdown';

import { useRecordCopyEventMutation, useSetCopyStatusMutation } from '../store/api';

/**
 * Copy/Paste Preferences Interface
 * Temporary type until backend types are updated
 */
interface CopyPastePreferences {
  include_header_on_copy_multiple?: boolean;
  html_formatting_on_copy?: boolean;
}

interface UserPreferencesWithCopy {
  copy_paste_preferences?: CopyPastePreferences;
}

export interface UseNoteCopyOptions {
  /** Note ID */
  noteId: string;

  /** Note data containing content and metadata (v2 API format) */
  noteData: NoteResponse | null | undefined;

  /** Encounter data with start_time, end_time, and duration (optional, used for header info) */
  encounterData?: Encounter;

  /** Array of selected section IDs (from Redux) */
  selectedSectionIds: string[];

  /** Map of section_id to dirty content (user edits not yet saved) */
  dirtySections?: Record<string, string>;

  /** Map of section IDs to BlockNote editor instances (for lossy conversion) */
  blockNoteEditors?: Map<string, AnyBlockNoteEditor>;
}

export interface UseNoteCopyReturn {
  /** Copy selected sections to clipboard */
  copySelectedSections: () => Promise<void>;

  /** Copy all sections to clipboard */
  copyAllSections: () => Promise<void>;

  /** Whether copy operation is in progress */
  isCopying: boolean;
}

/**
 * useNoteCopy hook
 *
 * Usage:
 * ```tsx
 * const { copySelectedSections, copyAllSections, isCopying } = useNoteCopy({
 *   noteId: '123',
 *   noteData: note,
 *   selectedSectionIds: ['section-1', 'section-3'],
 * });
 * ```
 */
export function useNoteCopy({
  noteId,
  noteData,
  encounterData,
  selectedSectionIds,
  dirtySections = {},
  blockNoteEditors,
}: UseNoteCopyOptions): UseNoteCopyReturn {
  const [isCopying, setIsCopying] = useState(false);
  const [copiedInSession, setCopiedInSession] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const { preferences } = useUserPreferences();
  const { note: noteMetrics } = useEventTracking();

  // Copy analytics mutations
  const [setCopyStatus] = useSetCopyStatusMutation();
  const [recordCopyEvent] = useRecordCopyEventMutation();

  /**
   * Internal copy implementation
   * @param copyAll - If true, copies all sections. If false, copies only selected sections.
   */
  const copySections = async (copyAll: boolean) => {
    if (!noteData || !noteData.content || !noteData.content.sections) {
      enqueueSnackbar('No note data available to copy', { variant: 'error' });
      return;
    }

    const totalSections = noteData.content.sections.length;

    // Check if there are any sections
    if (totalSections === 0) {
      enqueueSnackbar('No sections available to copy', { variant: 'warning' });
      return;
    }

    // Check if user is trying to copy selected sections but hasn't selected any
    if (!copyAll && selectedSectionIds.length === 0) {
      enqueueSnackbar('No sections selected. Please select sections to copy.', {
        variant: 'warning',
      });
      return;
    }

    setIsCopying(true);

    try {
      let htmlString = '';
      let plainTextString = '';
      const moduleIds: string[] = [];

      // Get user preferences for copying
      const prefs = preferences as UserPreferencesWithCopy;
      const includeHeaderOnCopyMultiple =
        prefs?.copy_paste_preferences?.include_header_on_copy_multiple ?? true;
      const includeHtml = prefs?.copy_paste_preferences?.html_formatting_on_copy ?? true;

      // Determine start time with fallback chain:
      // 1. encounterData.start_time
      // 2. encounterData.appointment.start_time
      // 3. noteData.transcription_start_timestamp
      // 4. noteData.time_uploaded
      let startTimeEpoch: number | null = null;
      if (encounterData?.start_time) {
        startTimeEpoch = new Date(encounterData.start_time).getTime() / 1000;
      } else if (encounterData?.appointment?.start_time) {
        startTimeEpoch = new Date(encounterData.appointment.start_time).getTime() / 1000;
      } else if (noteData.transcription_start_timestamp) {
        startTimeEpoch = noteData.transcription_start_timestamp;
      } else if (noteData.time_uploaded) {
        startTimeEpoch = noteData.time_uploaded;
      }

      // Calculate duration with fallback chain:
      // 1. Calculate from encounter start_time and end_time
      // 2. Use appointment duration (if encounter times not available)
      // 3. Fall back to transcription_length_seconds
      let durationSeconds: number | null = null;
      if (encounterData?.start_time && encounterData?.end_time) {
        const start = new Date(encounterData.start_time).getTime();
        const end = new Date(encounterData.end_time).getTime();
        durationSeconds = Math.floor((end - start) / 1000);
      } else if (encounterData?.appointment?.duration) {
        // Appointment duration is in minutes, convert to seconds
        durationSeconds = encounterData.appointment.duration * 60;
      } else if (noteData.transcription_length_seconds) {
        durationSeconds = noteData.transcription_length_seconds;
      }

      // Build header information using encounter-first fallback chain
      const headerInfo = {
        title: noteData.note_title ? `Note Title: ${noteData.note_title}` : undefined,
        provider: `Provider: ${noteData.issuing_user?.name || 'Unknown'}`,
        date: `Date: ${startTimeEpoch ? formatDate(startTimeEpoch, 'datelong') : 'N/A'}`,
        time: `Time: ${
          startTimeEpoch
            ? durationSeconds
              ? `${formatDate(startTimeEpoch, 'time')} – ${formatDate(startTimeEpoch + durationSeconds, 'time')}`
              : formatDate(startTimeEpoch, 'time')
            : 'N/A'
        }`,
        duration: `Duration: ${durationSeconds ? formatDuration(durationSeconds) : 'N/A'}`,
      };

      const headerPlainText =
        (headerInfo.title ? headerInfo.title + '\n' : '') +
        `${headerInfo.provider}\n` +
        `${headerInfo.date}\n` +
        `${headerInfo.time}\n` +
        `${headerInfo.duration}\n\n`;

      const headerHtml =
        (headerInfo.title ? `<p><strong>${headerInfo.title}</strong></p>` : '') +
        `<p><strong>${headerInfo.provider}</strong></p>` +
        `<p><strong>${headerInfo.date}</strong></p>` +
        `<p><strong>${headerInfo.time}</strong></p>` +
        `<p><strong>${headerInfo.duration}</strong></p><br/>`;

      // Add header if preference is enabled and copying multiple sections
      const copyingMultiple = copyAll || selectedSectionIds.length > 1;
      if (copyingMultiple && includeHeaderOnCopyMultiple) {
        plainTextString += headerPlainText;
        htmlString += headerHtml;
      }

      // Iterate over all sections
      for (const [sectionIndex, section] of noteData.content.sections.entries()) {
        // Check if the section should be copied
        if (selectedSectionIds.includes(section.section_id) || copyAll) {
          // Clean section name by removing text between parentheses
          const cleanedName = section.name.replace(/\s*\([^)]*\)/g, '');
          const sectionTitle = cleanedName;

          // Add section header
          plainTextString += `===\n${sectionTitle}\n===\n\n`;
          htmlString += htmlString ? `<hr/><h2>${sectionTitle}</h2>` : `<h2>${sectionTitle}</h2>`;

          // Use dirty content if available (user edits), otherwise fall back to cache
          const currentText = dirtySections[section.section_id]
            ?? section.edited_text
            ?? section.text
            ?? '';

          if (!currentText) {
            plainTextString += '\n\n';
            htmlString += '<br/>';
            continue;
          }

          // Try to use BlockNote editor for better fidelity (follows JotDocs pattern)
          const blockNoteEditor = blockNoteEditors?.get(section.section_id);

          if (blockNoteEditor) {
            // Use BlockNote's native lossy conversion (best fidelity)
            try {
              const sectionHtml = await blockNoteEditor.blocksToHTMLLossy(
                blockNoteEditor.document,
              );
              const sectionMarkdown = await blockNoteEditor.blocksToMarkdownLossy(
                blockNoteEditor.document,
              );

              htmlString += sectionHtml;
              plainTextString += sectionMarkdown + '\n\n';
            } catch (e) {
              console.error('Error converting BlockNote content:', e);
              // Fallback to marked if BlockNote conversion fails
              try {
                const sectionHtml = await marked(currentText);
                htmlString += sectionHtml;
                plainTextString +=
                  removeMd(currentText, {
                    stripListLeaders: false,
                    useImgAltText: false,
                  }) + '\n\n';
              } catch (markdownErr) {
                console.error('Error parsing markdown:', markdownErr);
                const textWithBreaks = currentText.replace(/\n/g, '<br/>');
                htmlString += `<div style="white-space: pre-wrap;">${textWithBreaks}</div>`;
                plainTextString += currentText + '\n\n';
              }
            }
          } else {
            // Fallback: Use marked/removeMd when editor not available
            try {
              const sectionHtml = await marked(currentText);
              htmlString += sectionHtml;
              plainTextString +=
                removeMd(currentText, {
                  stripListLeaders: false,
                  useImgAltText: false,
                }) + '\n\n';
            } catch (e) {
              console.error('Error parsing markdown:', e);
              const textWithBreaks = currentText.replace(/\n/g, '<br/>');
              htmlString += `<div style="white-space: pre-wrap;">${textWithBreaks}</div>`;
              plainTextString += currentText + '\n\n';
            }
          }

          htmlString += '<br/>';

          if (section.module_id) {
            moduleIds.push(section.module_id);
          }
        }
      }

      // Copy to clipboard
      const success = await copyRichText(htmlString, plainTextString, includeHtml);

      if (success) {
        // Show success message
        let successMessage = '';
        if (copyAll) {
          successMessage = `All ${totalSections} section${totalSections === 1 ? '' : 's'} copied to clipboard!`;
        } else {
          const copiedCount = selectedSectionIds.length;
          successMessage = `${copiedCount} selected section${copiedCount === 1 ? '' : 's'} copied to clipboard!`;
        }

        enqueueSnackbar(successMessage, {
          variant: 'success',
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        });

        // Track note copy event (Statsig)
        if (noteId) {
          noteMetrics.copied(noteId, {
            copy_type: copyAll ? 'all' : 'selected',
            sections_count: totalSections,
            sections_copied: copyAll ? totalSections : selectedSectionIds.length,
            include_header: copyingMultiple && includeHeaderOnCopyMultiple,
            include_html: includeHtml,
            source: 'button',
          });
        }

        // Track copy event (new telemetry model) - one event per section
        if (noteId && noteData.content.sections) {
          const copiedSections = copyAll
            ? noteData.content.sections
            : noteData.content.sections.filter((section: any) =>
                selectedSectionIds.includes(section.section_id),
              );

          const encounterId = noteData.encounter?.encounter_id;

          const context: TelemetryContext = {
            docId: `note:${noteId}`,
            getDocVersion: () => '0',
          };

          copiedSections.forEach((section: any) => {
            const sectionText = section.edited_text || section.text;
            buildSelectionCopied({
              context,
              lenSelected: sectionText.length,
              lenTotal: sectionText.length,
              modality: copyAll ? 'copy_all' : 'copy_selected',
              metadata: {
                note_id: noteId,
                section_id: section.section_id,
                module_id: section.module_id,
                section_title: section.name,
                encounter_id: encounterId,
              },
            });
          });
        }

        // Backend copy analytics
        if (noteId && moduleIds.length > 0) {
          // Record which sections were copied (fire-and-forget)
          try {
            await recordCopyEvent({
              noteId,
              moduleIds,
              copiedAll: copyAll,
            }).unwrap();
          } catch (err) {
            // Don't fail the copy if analytics fails, but report to Sentry
            console.warn('Failed to record copy event:', err);
            SentryService.captureException(err, {
              level: 'warning',
              tags: {
                feature: 'note-copy',
                operation: 'record-copy-event',
              },
              extra: {
                noteId,
                moduleIds,
                copiedAll: copyAll,
              },
            });
          }

          // Mark note as copied (once per session, once per note lifetime)
          const isNoteCopied = noteData?.is_copied as boolean | undefined;
          if (!copiedInSession && !isNoteCopied) {
            try {
              await setCopyStatus({ noteId }).unwrap();
              setCopiedInSession(true);
            } catch (err) {
              console.warn('Failed to set copy status:', err);
              SentryService.captureException(err, {
                level: 'warning',
                tags: {
                  feature: 'note-copy',
                  operation: 'set-copy-status',
                },
                extra: { noteId },
              });
            }
          }
        }
      } else {
        enqueueSnackbar('Failed to copy sections.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error copying sections:', error);
      enqueueSnackbar('Failed to copy sections.', {
        variant: 'error',
      });
    } finally {
      setIsCopying(false);
    }
  };

  const copySelectedSections = async () => {
    await copySections(false);
  };

  const copyAllSections = async () => {
    await copySections(true);
  };

  return {
    copySelectedSections,
    copyAllSections,
    isCopying,
  };
}
